/** @module csi/view/ToolbarPluginActions */
define([
	"dojo/_base/lang",
	"dojo/dom-class",
	"dijit/Menu",
	"dijit/MenuItem",
	"dijit/form/ComboButton",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/view/PluginFactory",
	"csi/widget/_DndHTML5Mixin",
	"csi/widget/MenuLabel",
	"./ToolbarPlugin"
], function(lang, domClass, Menu, MenuItem, ComboButton, _, html, locale, PluginFactory, _DndHTML5Mixin, MenuLabel, ToolbarPlugin) {

	/**
	 * This plugin places table's and gallery's actions on toolbar as "primary" ComboButton (button with drop down).
	 * Only root level actions will be shown. Nested groups actions will be shown inside groups.
	 * First action will be default and placed on the button; other actions will be in drop down.
	 */
	return PluginFactory.declare("ToolbarNewActions", [ComboButton, ToolbarPlugin, _DndHTML5Mixin], {
		"class": "csi-primary-btn",
		materialIcon: "add",

		disabled: true,

		/** @property {boolean} bVisible - determine if plugin should be shown (if it not empty and has some actions to show) */
		bVisible: false,

		/** @property {string} queryResultId - id of query result used for context of new actions (TODO: C8-12480) */
		queryResultId: null,

		/**
		 * Modify automation id if button is "Create" (BOM Placements and Specs)
		 */
		constructor: function(params) {
			var view = params.actionToolbar.view;
			if (view.newActionsButtonResource) {
				params["data-csi-automation"] += "Create";
			}
		},

		/**
		 * @override
		 * Hide plugin until it is determined to be enabled
		 */
		postCreatePlugin: function(ctx) {
			this.inherited(arguments);
			html.setVisible(this, false);
		},

		/**
		 * Set action context by setting html attributes on plugin's button node
		 * @property {QueryResult} qrRoot
		 * @property {Object} view
		 * @return {Action[]}
		 */
		setActionContext: function(qrRoot, view) {
			const {queryResultId} = this;
			const ctx = qrRoot.getChildContext();
			const qr = view.setQueryResultActionContext(qrRoot, ctx, this.domNode);

			if (queryResultId) {
				// cleanup previous result
				const qrPrevious = view.getQueryResult(queryResultId);
				view._removeQueryResult(qrPrevious);
			}

			this.queryResultId = qr.id;
			html.setDataAttribute(this.domNode, "ButtonNewActions", true);
		},

		/**
		 * Add action to drop down and set data-csi-action attribute
		 * @param {Action} a
		 * @param {boolean} bDisabled
		 */
		addChildMenuItem: function(a, bDisabled) {
			var self = this;
			var item = new MenuItem({
				label: locale.resource(a.getName()),
				disabled: bDisabled,
				onClick: function(e) {
					self._invokeAction(a, e);
				}
			});
			html.setElementAction(item.domNode, a.getId());
			this.dropDown.addChild(item);
		},

		/**
		 * Create drop down for "Create" Combo Button
		 * @param {Action[]} actions - "Placements" new actions
		 * @param {Action[]} placeholderActions - second group of new actions to add to "Create" button
		 */
		loadCreateButtonDropDown: function(actions, placeholderActions) {
			const view = this.actionToolbar.view;
			const newActionsButtonGroups = view.newActionsButtonGroups

			if (actions.length) {
				if (newActionsButtonGroups) {
					this.dropDown.addChild(new MenuLabel({
						label: newActionsButtonGroups[0]
					}));
				}

				actions.forEach(function(a) {
					this.addChildMenuItem(a, view.disableNewAction && view.disableNewAction(a));
				}, this);
			}

			if (placeholderActions.length) {
				if (newActionsButtonGroups) {
					this.dropDown.addChild(new MenuLabel({
						label: newActionsButtonGroups[1]
					}));
				}

				placeholderActions.forEach(function(a) {
					this.addChildMenuItem(a);
				}, this);
			}
		},

		/**
		 * Create drop down for normal split Combo Button
		 * @param {Action[]} actions
		 */
		loadSplitButtonDropDown: function(actions) {
			actions.forEach(function(a) {
				this.addChildMenuItem(a, false);
			}, this);
		},

		/**
		 * Populate button and drop down with accessible actions
		 * Hide button if empty
		 */
		setActions: function() {
			if (this.dropDown) {
				this.dropDown.destroyRecursive();
				delete this.dropDown;
			}

			this.dropDown = new Menu();

			const view = this.actionToolbar.view;
			const qrRoot = view.getQueryResult();
			const mainQueryLevel = view.getMainQueryLevel();
			const ctx = qrRoot.getChildContext();
			const actions = qrRoot.getNewActions(ctx, false, mainQueryLevel, true);
			const placeholderActions = view.getFilteredPlaceholderActions();

			this.setActionContext(qrRoot, view);
			this.bVisible = actions.length > 0 || placeholderActions.length > 0;

			if (this.bVisible) {
				if (placeholderActions.length || view.newActionsButtonResource) {
					domClass.add(this.domNode, "csi-hide-split");
					this.bVisible = true;
					this.set("label", locale.resource(view.newActionsButtonResource || "Create"));
					this.loadCreateButtonDropDown(actions, placeholderActions);
				} else {
					const action1 = this.defAction = actions.shift();
					if (action1.getId() === "AddDocuments") {
						this.createDocDropZone();
					}
					html.setVisible(this._buttonNode, actions.length);
					this._mixinActionProperties(this, action1);
					this.title = this.title || action1.getName();
					this.loadSplitButtonDropDown(actions);
				}
			}
		},

		canShowPlugin: function() {
			return this.bVisible;
		},

		/**
		 * @override
		 * Reset actions each time toolbar is enabled
		 */
		enablePlugin: function(ctx) {
			// set def action to null to prevent has access to action validation
			this.defAction = null;

			this.inherited(arguments);

			if (ctx && !this._opened) {
				this.setActions();
			}
		},

		/**
		 * PartMaterial to create drop down on click
		 * @param {Event} e
		 * @param {def/Action} [a]
		 */
		onClick: function(e, a, params) {
			if (this.actionToolbar.view.newActionsButtonResource) {
				this.loadAndOpenDropDown();
				this.dropDown.focus();
			} else {
				this.inherited(arguments);
			}
		},

		createDocDropZone: function() {
			this.dndHTML5Init(this.domNode, lang.hitch(this, this._onDropImage), null, false, null, false, true, true);
		},

		_onDropImage: function(files, view) {
			this.onClick(null, null, {droppedFiles: files});
		}
	});
});